// src/services/warehouse.service.js
import { env } from "../config/env";
import { createClient } from "./http";

/**
 * Admin Dashboard Backend (Warehouse service)
 * Base URL: env.WAREHOUSE_BASE_URL
 */
const api = createClient(env.WAREHOUSE_BASE_URL);

const normalizeStatus = (status) => {
  if (status === true) return "active";
  if (status === false) return "inactive";

  const s = String(status || "").trim().toLowerCase();
  if (!s) return "";

  // support common variants
  if (s === "activate") return "active";
  if (s === "deactivate") return "inactive";
  if (s === "enabled") return "active";
  if (s === "disabled") return "inactive";

  if (s !== "active" && s !== "inactive") {
    throw new Error("Invalid status. Use 'active' or 'inactive'.");
  }
  return s;
};


function unwrapWarehouseItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

async function listEveryWarehouse({ q = "", status = "", pageSize = 200 } = {}) {
  const limit = Math.min(Math.max(Number(pageSize) || 200, 1), 200);
  const collected = [];
  const seenIds = new Set();
  let page = 1;
  let expectedTotal = null;

  // Defensive cap prevents an accidental endless loop if an upstream response
  // returns broken pagination metadata.
  while (page <= 1000) {
    const params = { page, limit };
    if (q) params.q = q;
    if (status) params.status = String(status).toLowerCase();

    const response = await api.get("/api/admin/warehouses", { params });
    const rows = unwrapWarehouseItems(response);

    rows.forEach((warehouse) => {
      const id = String(warehouse?.id || warehouse?._id || "").trim();
      if (!id || seenIds.has(id)) return;
      seenIds.add(id);
      collected.push(warehouse);
    });

    const totalValue = Number(
      response?.total ?? response?.data?.total ?? response?.meta?.total
    );
    if (Number.isFinite(totalValue) && totalValue >= 0) {
      expectedTotal = totalValue;
    }

    const reachedTotal =
      expectedTotal !== null && collected.length >= expectedTotal;
    const reachedLastPage = rows.length < limit;

    if (reachedTotal || reachedLastPage || rows.length === 0) break;
    page += 1;
  }

  return collected;
}

export const warehouseService = {
  async listWarehouses({ page = 1, limit = 50, q = "", status = "" } = {}) {
    const params = { page, limit };
    if (q) params.q = q;
    if (status) params.status = String(status).toLowerCase();
    return api.get("/api/admin/warehouses", { params });
  },

  async listAllWarehouses({ q = "", status = "", pageSize = 200 } = {}) {
    return listEveryWarehouse({ q, status, pageSize });
  },

  async createWarehouse(payload) {
    return api.post("/api/admin/warehouses", payload);
  },

  async getWarehouse(id) {
    return api.get(`/api/admin/warehouses/${id}`);
  },

  async updateWarehouse(id, payload) {
    return api.patch(`/api/admin/warehouses/${id}`, payload);
  },

  /**
   * PATCH /api/admin/warehouses/:id/status
   * Accepts:
   *   - "active" | "inactive"
   *   - true | false
   *   - { status: "active" | "inactive" }
   */
  async permanentCloseWarehouse(id, payload = {}) {
    return api.patch(`/api/admin/warehouses/${id}/permanent-close`, payload);
  },

  async reopenWarehouse(id, payload = { status: "active" }) {
    return api.patch(`/api/admin/warehouses/${id}/reopen`, payload);
  },

  async setWarehouseStatus(id, statusOrObj) {
    const raw =
      statusOrObj && typeof statusOrObj === "object"
        ? statusOrObj.status
        : statusOrObj;

    const status = normalizeStatus(raw);
    return api.patch(`/api/admin/warehouses/${id}/status`, { status });
  },

  async listWarehouseEmployeesLookup() {
    return api.get("/api/admin/lookups/warehouse-employees");
  },
};